// ============================================================================
// ui/eyedropper.js — element-aware colour eyedropper
// ============================================================================
// The browser's native EyeDropper samples a RAW SCREEN PIXEL, so a colour swatch's
// glass gradient (lighter top, darker bottom) made the picked colour depend on WHERE
// you clicked. This picker instead reads the TRUE colour of whatever is under the
// cursor — a colour swatch → its own background hex; the 3D plate <canvas> → the
// rendered pixel there; any other element → its computed background. Crucially it
// NEVER restyles the swatches. Works in every browser (no EyeDropper API needed).
// ============================================================================

import { normalizeHex } from '../design-profile.js';

const hx = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

/** Parse an `rgb()/rgba()` string → hex, or null if fully transparent / unparseable. */
function rgbStrToHex(str) {
  const m = /rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+))?/i.exec(String(str || ''));
  if (!m) return null;
  if (m[4] != null && Number(m[4]) === 0) return null; // fully transparent → not a colour
  return '#' + hx(+m[1]) + hx(+m[2]) + hx(+m[3]);
}

/** Read one pixel from a (WebGL, preserveDrawingBuffer) canvas at a client point. */
function samplePlatePixel(canvas, clientX, clientY) {
  try {
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const px = Math.round(((clientX - rect.left) / rect.width) * canvas.width);
    const py = Math.round(((clientY - rect.top) / rect.height) * canvas.height);
    const tmp = document.createElement('canvas');
    tmp.width = 1; tmp.height = 1;
    const tctx = tmp.getContext('2d', { willReadFrequently: true });
    tctx.drawImage(canvas, px, py, 1, 1, 0, 0, 1, 1);
    const d = tctx.getImageData(0, 0, 1, 1).data;
    if (d[3] === 0) return null;
    return '#' + hx(d[0]) + hx(d[1]) + hx(d[2]);
  } catch { return null; }
}

/** The colour of a hard-stop linear-gradient at horizontal fraction f (0..1). Lets the eyedropper
 *  grab the correct HALF of a divided swatch — the "Original" logo-fill chip (yellow | green). */
export function gradientColorAt(bgImage, f) {
  const stops = [];
  const re = /(rgba?\([^)]*\)|#[0-9a-fA-F]{3,8})\s*([\d.]+)%/g;
  let m;
  while ((m = re.exec(String(bgImage || '')))) stops.push({ color: m[1], pos: +m[2] });
  if (!stops.length) return null;
  const p = Math.max(0, Math.min(1, f)) * 100;
  for (const s of stops) if (p <= s.pos) return s.color;
  return stops[stops.length - 1].color;
}

/** The TRUE colour under (x,y): a swatch's own hex → the plate pixel → a computed bg. */
function colorUnder(x, y) {
  const el = document.elementFromPoint(x, y);
  if (!el) return null;
  // A colour swatch → its background colour (independent of the ::after sheen / box-shadow).
  const chip = el.closest && el.closest('.wd-cchip:not(.wd-cchip--tool)');
  if (chip) {
    const cs = getComputedStyle(chip);
    // A DIVIDED swatch (the "Original" chip) paints its tones via a background-IMAGE gradient — read
    // the tone under the cursor's x FIRST (left half = one colour, right half = another). This must
    // come BEFORE the solid read: the chip is a <button>, so its background-COLOR is the browser
    // default (#F0F0F0), not the shown colour — reading that would grab near-white everywhere.
    if (cs.backgroundImage && cs.backgroundImage.indexOf('gradient') !== -1) {
      const r = chip.getBoundingClientRect();
      const g = gradientColorAt(cs.backgroundImage, r.width > 0 ? (x - r.left) / r.width : 0.5);
      if (g) { const c = g[0] === '#' ? normalizeHex(g) : rgbStrToHex(g); if (c) return normalizeHex(c); }
    }
    const solid = rgbStrToHex(cs.backgroundColor);
    if (solid) return normalizeHex(solid);
  }
  // The 3D plate canvas → the rendered pixel there.
  if (el.tagName === 'CANVAS') { const c = samplePlatePixel(el, x, y); if (c) return normalizeHex(c); }
  // Otherwise the nearest element with a non-transparent background.
  for (let n = el; n && n !== document.documentElement; n = n.parentElement) {
    const c = rgbStrToHex(getComputedStyle(n).backgroundColor);
    if (c) return normalizeHex(c);
  }
  return null;
}

/**
 * Open the element-aware eyedropper. Resolves the picked hex, or null on cancel
 * (Esc / right-click). Shows a crosshair + a colour-preview dot at the cursor.
 * @param {HTMLElement} [mount] where to attach the overlay (defaults to document.body)
 * @returns {Promise<string|null>}
 */
export function pickColorWithEyedropper(mount) {
  return new Promise((resolve) => {
    const host = mount || document.body;
    const overlay = document.createElement('div');
    overlay.className = 'wd-eyedrop-overlay';
    const preview = document.createElement('div');
    preview.className = 'wd-eyedrop-preview';
    preview.style.visibility = 'hidden';
    overlay.appendChild(preview);
    host.appendChild(overlay);

    // elementFromPoint must see THROUGH the overlay — toggle its hit-testing off around the read.
    const read = (x, y) => {
      overlay.style.pointerEvents = 'none';
      const c = colorUnder(x, y);
      overlay.style.pointerEvents = '';
      return c;
    };
    const move = (e) => {
      const c = read(e.clientX, e.clientY);
      preview.style.transform = `translate(${e.clientX + 16}px, ${e.clientY + 16}px)`;
      preview.style.background = c || 'transparent';
      preview.style.visibility = c ? 'visible' : 'hidden';
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('keydown', key, true);
      overlay.remove();
    };
    const finish = (val) => { cleanup(); resolve(val); };
    const down = (e) => {
      if (e.button === 2) { e.preventDefault(); return finish(null); } // right-click cancels
      e.preventDefault();
      e.stopPropagation();
      // Eat the trailing `click` so it can't also fire the swatch/button under the cursor.
      const eat = (ev) => { ev.preventDefault(); ev.stopPropagation(); window.removeEventListener('click', eat, true); };
      window.addEventListener('click', eat, true);
      setTimeout(() => window.removeEventListener('click', eat, true), 350);
      finish(read(e.clientX, e.clientY));
    };
    const key = (e) => { if (e.key === 'Escape') { e.preventDefault(); finish(null); } };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('keydown', key, true);
  });
}
